import React from 'react'
import classNames from 'classnames'
import { flowRight, get } from 'lodash'
import { useTranslation } from 'react-i18next'
import { graphql } from '@apollo/client/react/hoc'
import PropTypes from 'prop-types'
import { getVehicleShareUrl } from 'lib/utils/store/vehicle'
import { getLocale } from 'lib/utils/getLocale'

import VehicleProfileShareForm from './VehicleProfileShareForm/VehicleProfileShareForm'
import LoadingIndicator from 'components/LoadingIndicator'
import ErrorComponent from 'components/Errors/ErrorComponent'
import VehicleProfileShareLogic from './VehicleProfileShareLogic'
import SomeShare from 'components/UI/SomeShare/SomeShare'
import VehicleProfileShareWithEmail from './VehicleProfileShareWithEmail/VehicleProfileShareWithEmail'
import ExtendedInput from 'components/Forms/FormElements/ExtendedInput/ExtendedInput'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import Button from 'components/UI/Button'
import VehicleProfileShareToggle from './VehicleProfileShareToggle/VehicleProfileShareToggle'

import getVehicleShare from 'graphql/vehicle/getVehicleShare.gql'

import './vehicle-profile-share.pcss'

const VehicleProfileShare = ({ vehicleData, modalProps }) => {
    const { loading, error, vehicle } = vehicleData
    const { t, i18n } = useTranslation(['vehicleProfile'])

    if (loading) return <LoadingIndicator />
    if (error) return <ErrorComponent error={error} />

    const shareLink = get(vehicle, 'shareLink', null)
    const createNewShareLink = !shareLink
    const shareUrl = shareLink
        ? `${window.location.origin}${getLocale(i18n.language)}${getVehicleShareUrl(vehicle.id, shareLink)}`
        : null

    return (
        <div className="vehicle-profile-share">
            <VehicleProfileShareLogic
                initState={{ loading: createNewShareLink }}
                onMount={async (profileShareLogic) => {
                    if (createNewShareLink) {
                        await profileShareLogic.createVehicleShareHash(vehicle.id, '', 'BASIC')
                    }
                }}
            >
                {(vehicleProfileShareLogic) => {
                    if (vehicleProfileShareLogic.loading) return <LoadingIndicator />
                    if (vehicleProfileShareLogic.error) return <ErrorComponent error={vehicleProfileShareLogic.error} />
                    return (
                        <>
                            <VehicleProfileShareToggle
                                isActive={shareLink.isActive}
                                linkId={shareLink.id}
                            />
                            <div
                                className={classNames({
                                    'vehicle-profile-share--require-activation': !shareLink.isActive,
                                })}
                            >
                                <VehicleProfileShareForm
                                    key={shareLink.isActive ? 'active' : 'inactive'}
                                    vehicleId={vehicle.id}
                                    shareLink={shareLink}
                                    vehicleProfileShareLogic={vehicleProfileShareLogic}
                                />
                                <FormElementGroup>
                                    <SomeShare url={shareUrl} />
                                    <VehicleProfileShareWithEmail
                                        vehicleProfileShareLogic={vehicleProfileShareLogic}
                                        shareLinkId={shareLink.id}
                                    />
                                </FormElementGroup>
                                <FormElementGroup>
                                    <label htmlFor="profile-share-link">{t('vehicleProfile:share.shareLinkLabel')}</label>
                                    <ExtendedInput>
                                        <input
                                            id="profile-share-link"
                                            type="text"
                                            name="profile-share-link"
                                            readOnly
                                            value={shareUrl}
                                            onClick={(e) => {
                                                e.target.select()
                                            }}
                                        />
                                    </ExtendedInput>
                                </FormElementGroup>
                            </div>
                        </>
                    )
                }}
            </VehicleProfileShareLogic>
            <div className="vehicle-profile-share__actions">
                <Button
                    className="button button--secondary"
                    trackName="closeModal"
                    onClick={() => modalProps.closeModal()}
                >
                    {t('common:close')}
                </Button>
            </div>
        </div>
    )
}

VehicleProfileShare.propTypes = {
    vehicleId: PropTypes.string.isRequired,
    modalProps: PropTypes.object.isRequired,
}

export default flowRight(
    graphql(getVehicleShare, {
        name: 'vehicleData',
        options: ({ vehicleId }) => ({
            variables: {
                vehicleId: vehicleId,
            },
        })
    })
)(VehicleProfileShare)
